import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { NAV_LINKS, scrollToId } from "@/data";
import { Logo } from "./Logo";

export const Navbar = () => {
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 40);
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    const go = (id) => {
        setOpen(false);
        scrollToId(id);
    };

    return (
        <>
            <motion.header
                data-testid="main-navbar"
                initial={{ y: -80, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1], delay: 0.4 }}
                className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-500 ${
                    scrolled
                        ? "backdrop-blur-xl bg-navy/85 border-b border-white/10"
                        : "bg-transparent border-b border-transparent"
                }`}
            >
                <div className="max-w-7xl mx-auto px-6 lg:px-10 h-[72px] flex items-center justify-between">
                    <button
                        data-testid="nav-logo-button"
                        onClick={() => window.__lenis?.scrollTo(0, { duration: 1.4 })}
                        className="group"
                    >
                        <Logo />
                    </button>

                    <nav className="hidden lg:flex items-center gap-7">
                        {NAV_LINKS.map((l) => (
                            <button
                                key={l.id}
                                data-testid={`nav-link-${l.id}`}
                                onClick={() => go(l.id)}
                                className="text-[13px] font-semibold text-white/70 hover:text-brand transition-colors duration-300 tracking-wide"
                            >
                                {l.label}
                            </button>
                        ))}
                    </nav>

                    <div className="flex items-center gap-3">
                        <button
                            data-testid="nav-cta-button"
                            onClick={() => go("contato")}
                            className="hidden sm:inline-flex items-center gap-2 bg-brand hover:bg-brand-hover text-white text-sm font-bold px-5 py-2.5 rounded-full transition-colors duration-300"
                        >
                            Solicitar orçamento
                        </button>
                        <button
                            data-testid="nav-mobile-menu-button"
                            onClick={() => setOpen(true)}
                            className="lg:hidden text-white p-2"
                            aria-label="Abrir menu"
                        >
                            <Menu className="w-6 h-6" />
                        </button>
                    </div>
                </div>
            </motion.header>

            <AnimatePresence>
                {open && (
                    <motion.div
                        data-testid="mobile-menu"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.3 }}
                        className="fixed inset-0 z-[60] bg-navy noise-bg flex flex-col"
                    >
                        <div className="flex justify-between items-center p-6">
                            <Logo />
                            <button
                                data-testid="mobile-menu-close-button"
                                onClick={() => setOpen(false)}
                                className="text-white p-2"
                                aria-label="Fechar menu"
                            >
                                <X className="w-7 h-7" />
                            </button>
                        </div>
                        <nav className="flex-1 flex flex-col justify-center px-10 gap-2">
                            {NAV_LINKS.map((l, i) => (
                                <motion.button
                                    key={l.id}
                                    data-testid={`mobile-nav-link-${l.id}`}
                                    initial={{ opacity: 0, x: -24 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    transition={{ delay: 0.08 * i, duration: 0.5 }}
                                    onClick={() => go(l.id)}
                                    className="text-left font-display text-3xl font-bold text-white hover:text-brand transition-colors duration-300 py-2"
                                >
                                    {l.label}
                                </motion.button>
                            ))}
                            <motion.button
                                data-testid="mobile-nav-cta-button"
                                initial={{ opacity: 0, y: 16 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: 0.6 }}
                                onClick={() => go("contato")}
                                className="mt-8 bg-brand text-white font-bold px-8 py-4 rounded-full w-fit"
                            >
                                Solicitar orçamento
                            </motion.button>
                        </nav>
                    </motion.div>
                )}
            </AnimatePresence>
        </>
    );
};
